import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { CheckSquare, ArrowRightCircle, X } from 'lucide-react';
import api from '../lib/api';
import InlineEditCell from './InlineEditCell';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SIZE_ORDER = ['S','M','L','XL','2XL','3XL','4XL','5XL','6XL','7XL','8XL','9XL','Free Size','32','34','36','38','40','42'];
const fmtDate = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 && p[0].length === 4 ? `${p[2]}-${p[1]}-${p[0].slice(-2)}` : d; };
const STATUS_COLORS = { pending: 'bg-yellow-100 text-yellow-700', confirmed: 'bg-blue-100 text-blue-700', partial_fulfilled: 'bg-amber-100 text-amber-700', fulfilled: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700', closed: 'bg-gray-200 text-gray-700', draft: 'bg-slate-100 text-slate-600' };
const statusLabel = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const SalesOrderDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [so, setSo] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [agents, setAgents] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareFormat, setShareFormat] = useState('pdf');
  const [closingItemId, setClosingItemId] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [searchDesign, setSearchDesign] = useState('');
  const [sortBy, setSortBy] = useState('');

  const canManage = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';

  // Selection key format: "itemId::color"
  const makeKey = (itemId, color) => `${itemId}::${color}`;

  const toggleColorSelect = (itemId, color) => {
    const key = makeKey(itemId, color);
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Build selectable colour rows from items with pending variants
  const selectableKeys = (() => {
    const keys = [];
    (so?.items || []).forEach(item => {
      if (item.status === 'cancelled' || item.status === 'closed') return;
      const colorSet = new Set();
      (item.variants || []).forEach(v => {
        if ((v.qty_ordered || 0) > (v.qty_dispatched || 0)) colorSet.add(v.color || 'Other');
      });
      colorSet.forEach(color => keys.push(makeKey(item.id, color)));
    });
    return keys;
  })();

  const toggleSelectAll = () => {
    if (selectedKeys.size === selectableKeys.length && selectableKeys.length > 0) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(selectableKeys));
    }
  };

  const processSelectedItems = () => {
    if (selectedKeys.size === 0) { toast.error('Select at least one colour'); return; }
    // Group selected keys by item, filter variants to only selected colours
    const itemColorMap = {};
    selectedKeys.forEach(key => {
      const [itemId, color] = key.split('::');
      if (!itemColorMap[itemId]) itemColorMap[itemId] = new Set();
      itemColorMap[itemId].add(color);
    });
    const selectedItems = (so.items || [])
      .filter(item => itemColorMap[item.id])
      .map(item => ({
        ...item,
        variants: (item.variants || []).filter(v => itemColorMap[item.id].has(v.color || 'Other')),
      }))
      .filter(item => item.variants.length > 0);
    navigate(`/sales-order/${so.id}/process-items`, {
      state: { soData: so, selectedItems },
    });
  };

  const loadData = useCallback(async () => {
    try {
      const [soRes, custRes, brandRes, agentRes, distRes, eventRes] = await Promise.allSettled([
        api.get(`/sales-orders/${id}`), api.get('/customers'), api.get('/brands'),
        api.get('/agents'), api.get('/distributors'), api.get('/events'),
      ]);
      if (soRes.status === 'fulfilled') setSo(soRes.value.data); else throw new Error('SO not found');
      if (custRes.status === 'fulfilled') setCustomers(custRes.value.data);
      if (brandRes.status === 'fulfilled') setBrands(brandRes.value.data);
      if (agentRes.status === 'fulfilled') setAgents(agentRes.value.data);
      if (distRes.status === 'fulfilled') setDistributors(distRes.value.data);
      if (eventRes.status === 'fulfilled') setEvents(eventRes.value.data);
    } catch { toast.error('Failed to load SO'); navigate('/sales-orders'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const getCustomerName = (cid) => { const c = customers.find(x => x.id === cid); if (!c) return 'Unknown'; return c.company_name || c.name || 'Unknown'; };
  const getBrandName = (bid) => brands.find(b => b.id === bid)?.name || '';

  const totalOrdered = (so?.items || []).reduce((s, i) => s + (i.variants || []).reduce((vs, v) => vs + (v.qty_ordered || 0), 0), 0);
  const totalDispatched = (so?.items || []).reduce((s, i) => s + (i.variants || []).reduce((vs, v) => vs + (v.qty_dispatched || 0), 0), 0);
  const totalPending = (so?.items || []).reduce((s, i) => s + (i.variants || []).reduce((vs, v) => vs + Math.max(0, (v.qty_ordered || 0) - (v.qty_dispatched || 0)), 0), 0);
  const pct = totalOrdered > 0 ? Math.round(totalDispatched / totalOrdered * 100) : 0;

  const deleteSO = async () => {
    if (!window.confirm('Delete this Sales Order? This cannot be undone.')) return;
    try { await api.delete(`/sales-orders/${so.id}`); toast.success('SO deleted'); navigate('/sales-orders'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const inlineUpdateVariant = async (itemId, variantIdx, field, value) => {
    if (!canManage || so.status === 'cancelled' || so.status === 'closed') return;
    const updatedSO = JSON.parse(JSON.stringify(so));
    const item = updatedSO.items.find(i => i.id === itemId);
    if (!item || !item.variants[variantIdx]) return;
    item.variants[variantIdx][field] = value;
    // Recalculate totals
    let total = 0, totalQty = 0;
    for (const it of updatedSO.items) {
      for (const v of it.variants || []) {
        total += (v.qty_ordered || 0) * (v.wsp || 0);
        totalQty += v.qty_ordered || 0;
      }
    }
    updatedSO.total_amount = total;
    updatedSO.total_qty = totalQty;
    try {
      await api.put(`/sales-orders/${so.id}`, { items: updatedSO.items });
      setSo(updatedSO);
      toast.success('Updated');
    } catch { toast.error('Failed to update'); }
  };

  const executeShare = async (format) => {
    const endpoint = format === 'pdf' ? `/sales-orders/${so.id}/pdf` : `/sales-orders/${so.id}/excel`;
    const mimeType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const ext = format === 'pdf' ? '.pdf' : '.xlsx';
    try {
      const res = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mimeType }));
      const a = document.createElement('a'); a.href = url; a.download = `${so.so_number}${ext}`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded!`);
    } catch { toast.error('Failed'); }
    setShareOpen(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div></div>;
  if (!so) return null;

  const allItems = so.items || [];
  const usedSizes = new Set();
  allItems.forEach(item => (item.variants || []).forEach(v => usedSizes.add(v.size)));
  const activeSizes = SIZE_ORDER.filter(s => usedSizes.has(s));

  let sr = 0, grandQty = 0, grandAmount = 0;
  const rows = [];
  allItems.forEach((item) => {
    const colorMap = {}, colorOrder = [];
    (item.variants || []).forEach(v => {
      const c = v.color || 'Other';
      if (!colorMap[c]) { colorMap[c] = { sizes: {}, dispatched: {}, qty: 0, wsps: {}, discounts: {} }; colorOrder.push(c); }
      colorMap[c].sizes[v.size] = (colorMap[c].sizes[v.size] || 0) + (v.qty_ordered || 0);
      colorMap[c].dispatched[v.size] = (colorMap[c].dispatched[v.size] || 0) + (v.qty_dispatched || 0);
      colorMap[c].qty += (v.qty_ordered || 0);
      colorMap[c].wsps[v.size] = v.wsp || 0;
      colorMap[c].discounts[v.size] = v.discount_percent || 0;
    });
    const itemStartIdx = rows.length;
    colorOrder.sort((a, b) => (a || '').localeCompare(b || '')).forEach(color => {
      sr++;
      const d = colorMap[color];
      let amount = 0;
      activeSizes.forEach(s => {
        const q = d.sizes[s] || 0; const w = d.wsps[s] || 0; const disc = d.discounts[s] || 0;
        amount += q * w * (1 - disc / 100);
      });
      grandQty += d.qty; grandAmount += amount;
      const totalDisp = Object.values(d.dispatched).reduce((s, v) => s + v, 0);
      const wspGroups = [];
      let currentGroup = null;
      activeSizes.forEach(s => {
        if ((d.sizes[s] || 0) === 0) return;
        const wsp = d.wsps[s] || 0;
        if (currentGroup && currentGroup.wsp === wsp) { currentGroup.sizes.push(s); }
        else { currentGroup = { wsp, sizes: [s] }; wspGroups.push(currentGroup); }
      });
      rows.push({ sr, item, color, sizes: d.sizes, dispatched: d.dispatched, wsps: d.wsps, qty: d.qty, amount, totalDispatched: totalDisp, wspGroups, isFirstOfItem: false, itemRowSpan: 0 });
    });
    // Mark first row and set rowSpan (each color row = 2 actual <tr> rows)
    const colorCount = rows.length - itemStartIdx;
    if (colorCount > 0) {
      rows[itemStartIdx].isFirstOfItem = true;
      rows[itemStartIdx].itemRowSpan = colorCount * 2; // 2 tr per color row (main + wsp row)
    }
  });

  const closeItem = async (itemId) => {
    if (!window.confirm('Close this item? Pending sizes will be marked as closed and won\'t appear in Ready to Dispatch.')) return;
    setClosingItemId(itemId);
    try {
      await api.post(`/sales-orders/${so.id}/items/${itemId}/close`);
      toast.success('Item closed successfully');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close item');
    } finally {
      setClosingItemId(null);
    }
  };

  const showActionCol = canManage && so.status !== 'cancelled' && so.status !== 'fulfilled' && so.status !== 'closed';

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6 animate-fadeIn" data-testid="so-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/sales-orders')} data-testid="so-detail-back" className="text-gray-500 hover:text-gray-900">
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>Back
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 font-mono" data-testid="so-detail-number">{so.so_number}</h1>
              <Badge className={`${STATUS_COLORS[so.status] || 'bg-gray-100'} text-sm`} data-testid="so-detail-status">{statusLabel(so.status)}</Badge>
            </div>
            <h2 className="text-xl font-bold text-blue-800 mt-1" data-testid="so-detail-customer">{getCustomerName(so.customer_id)}</h2>
            <div className="flex items-center gap-3 flex-wrap mt-1 text-sm text-gray-500">
              <span>{fmtDate(so.so_date)}</span>
              {(() => { const c = customers.find(x => x.id === so.customer_id); return c ? (
                <>
                  {c.city && <span>{c.city}{c.state ? `, ${c.state}` : ''}</span>}
                  {c.agent_id && <span className="text-indigo-600">Agent: {agents.find(a => a.id === c.agent_id)?.name || ''}</span>}
                  {c.distributor_id && <span className="text-teal-600">Dist: {distributors.find(d => d.id === c.distributor_id)?.company_name || ''}</span>}
                </>
              ) : null; })()}
              {so.event_id && <span className="text-pink-600">{events.find(e => e.id === so.event_id)?.name || ''}</span>}
              {so.occasion && <span className="capitalize">{so.occasion.replace(/_/g, ' ')}</span>}
              {so.payment_mode && <span className="capitalize">{so.payment_mode}</span>}
              {so.transport_name && <span>Transport: {so.transport_name}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {so.approval_status === 'approved' && (
            <Button size="sm" variant="outline" onClick={() => setShareOpen(true)} data-testid="so-detail-share">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>Download
            </Button>
          )}
          {canManage && (
            <>
              <Button size="sm" variant="outline" className="text-blue-600 border-blue-300" onClick={() => navigate('/create-sales-order', { state: { editSO: so } })} data-testid="so-detail-edit">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>Edit
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={deleteSO} data-testid="so-detail-delete">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="so-detail-summary">
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Total Amount</p><p className="text-xl font-bold text-gray-900">₹{(so.total_amount || 0).toLocaleString('en-IN')}</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Ordered</p><p className="text-xl font-bold text-blue-700">{totalOrdered} pcs</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Dispatched</p><p className="text-xl font-bold text-green-700">{totalDispatched} pcs</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">{so.status === 'closed' ? 'Short Closed' : 'Pending'}</p><p className={`text-xl font-bold ${so.status === 'closed' ? 'text-orange-600' : 'text-red-600'}`}>{totalPending} pcs</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-gray-500 mb-1">Progress</p>
          <p className="text-xl font-bold text-emerald-700">{pct}%</p>
          <div className="w-full h-1.5 bg-gray-200 rounded-full mt-1.5"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, pct)}%` }}></div></div>
        </CardContent></Card>
      </div>

      {/* Create SE CTA */}
      {canManage && so.status !== 'cancelled' && so.status !== 'fulfilled' && so.status !== 'closed' && so.approval_status === 'approved' && (
        <div className="flex gap-3 flex-wrap">
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => navigate('/create-sales-entry', { state: { fromSO: so } })} data-testid="so-detail-create-se">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>Create Sales Entry
          </Button>
          {(so.status === 'partial_fulfilled' || so.status === 'confirmed') && totalDispatched > 0 && totalDispatched < totalOrdered && (
            <Button variant="outline" className="border-orange-400 text-orange-700" onClick={async () => {
              if (!window.confirm(`Close balance of ${totalPending} pcs?`)) return;
              try { await api.post(`/sales-orders/${so.id}/close-balance`); toast.success('Balance closed!'); loadData(); }
              catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
            }} data-testid="so-detail-close-balance">Close Balance ({totalPending} pcs)</Button>
          )}
        </div>
      )}

      {/* Info */}
      {(so.transport_name || so.notes || so.created_by_name || so.dispatch_date) && (
        <div className="flex gap-6 flex-wrap text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
          {so.dispatch_date && <span>Dispatch: <strong>{fmtDate(so.dispatch_date)}</strong></span>}
          {so.transport_name && <span>Transport: <strong>{so.transport_name}</strong></span>}
          {so.occasion && <span>Occasion: <strong>{so.occasion}</strong></span>}
          {so.payment_mode && <span>Payment: <strong>{so.payment_mode}</strong></span>}
          {so.created_by_name && <span>Created by: <strong>{so.created_by_name}</strong></span>}
          {so.notes && <span className="italic text-gray-500">Notes: {so.notes}</span>}
        </div>
      )}

      {/* Pivot Table */}
      <Card data-testid="so-detail-items">
        <CardContent className="p-0">
          {/* Search, Sort, Bulk Size */}
          <div className="px-4 py-2 border-b bg-gray-50 flex items-center gap-3 flex-wrap">
            <input
              type="text"
              value={searchDesign}
              onChange={(e) => setSearchDesign(e.target.value)}
              placeholder="Search design code..."
              className="h-8 w-48 px-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
              data-testid="so-detail-search"
            />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-8 px-2 text-xs border border-gray-300 rounded-md focus:outline-none"
              data-testid="so-detail-sort"
            >
              <option value="">Default Order</option>
              <option value="design_asc">Design A→Z</option>
              <option value="design_desc">Design Z→A</option>
              <option value="qty_desc">Qty High→Low</option>
              <option value="qty_asc">Qty Low→High</option>
              <option value="amount_desc">Amount High→Low</option>
              <option value="color_asc">Colour A→Z</option>
            </select>
            {canManage && so.status !== 'cancelled' && so.status !== 'fulfilled' && so.status !== 'closed' && (
              <>
                <select
                  className="h-8 px-2 text-xs border border-red-200 rounded-md bg-red-50/50 focus:outline-none"
                  defaultValue=""
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const size = e.target.value;
                    e.target.value = '';
                    // Navigate to Edit SO with the size removed
                    const editSO = JSON.parse(JSON.stringify(so));
                    editSO.items = editSO.items.map(item => ({
                      ...item,
                      variants: (item.variants || []).filter(v => v.size !== size),
                    })).filter(item => item.variants.length > 0);
                    navigate('/create-sales-order', { state: { editSO } });
                    toast.info(`Editing SO — size "${size}" removed. Review and save.`);
                  }}
                  data-testid="so-bulk-remove-size"
                >
                  <option value="">Remove size...</option>
                  {activeSizes.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                  className="h-8 px-2 text-xs border border-green-200 rounded-md bg-green-50/50 focus:outline-none"
                  defaultValue=""
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const size = e.target.value;
                    e.target.value = '';
                    // Navigate to Edit SO with the size added to all items/colors
                    const editSO = JSON.parse(JSON.stringify(so));
                    editSO.items = editSO.items.map(item => {
                      const existingSizes = new Set((item.variants || []).map(v => `${v.color}::${v.size}`));
                      const colors = [...new Set((item.variants || []).map(v => v.color))];
                      const newVariants = [...(item.variants || [])];
                      colors.forEach(color => {
                        if (!existingSizes.has(`${color}::${size}`)) {
                          const ref = item.variants.find(v => v.color === color) || {};
                          newVariants.push({ ...ref, size, qty_ordered: 0, qty_dispatched: 0 });
                        }
                      });
                      return { ...item, variants: newVariants };
                    });
                    navigate('/create-sales-order', { state: { editSO } });
                    toast.info(`Editing SO — size "${size}" added. Set quantities and save.`);
                  }}
                  data-testid="so-bulk-add-size"
                >
                  <option value="">Add size...</option>
                  {SIZE_ORDER.filter(s => !activeSizes.includes(s)).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="so-excel-table">
              <thead>
                <tr className="bg-[#1E1E23] text-white text-xs uppercase tracking-wider">
                  <th className="px-2 py-2.5 text-center font-medium border border-gray-700 w-10">
                    <Checkbox
                      checked={selectedKeys.size > 0 && selectedKeys.size === selectableKeys.length}
                      onCheckedChange={toggleSelectAll}
                      className="border-gray-400 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                      data-testid="so-select-all-items"
                    />
                  </th>
                  <th className="px-3 py-2.5 text-center font-medium border border-gray-700 w-10">SR</th>
                  <th className="px-2 py-2.5 text-center font-medium border border-gray-700 w-16">Image</th>
                  <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Design</th>
                  <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Brand</th>
                  <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Colour</th>
                  {activeSizes.map(s => <th key={s} className="px-2 py-2.5 text-center font-medium border border-gray-700 min-w-[42px]">{s}</th>)}
                  <th className="px-3 py-2.5 text-center font-medium border border-gray-700">Qty</th>
                  <th className="px-3 py-2.5 text-right font-medium border border-gray-700">Amount</th>
                  <th className="px-3 py-2.5 text-center font-medium border border-gray-700">Dispatched</th>
                  {canManage && so.status !== 'cancelled' && so.status !== 'fulfilled' && so.status !== 'closed' && (
                    <th className="px-2 py-2.5 text-center font-medium border border-gray-700 w-24">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let displayRows = rows.filter(r => !searchDesign || r.item.design_code.toLowerCase().includes(searchDesign.toLowerCase()));
                  if (sortBy) {
                    displayRows = [...displayRows].sort((a, b) => {
                      switch (sortBy) {
                        case 'design_asc': return a.item.design_code.localeCompare(b.item.design_code, undefined, { numeric: true });
                        case 'design_desc': return b.item.design_code.localeCompare(a.item.design_code, undefined, { numeric: true });
                        case 'qty_desc': return b.qty - a.qty;
                        case 'qty_asc': return a.qty - b.qty;
                        case 'amount_desc': return b.amount - a.amount;
                        case 'color_asc': return a.color.localeCompare(b.color);
                        default: return 0;
                      }
                    });
                  }
                  return displayRows;
                })().map((r, ri) => {
                  const firstImg = (r.item.images || [])[0];
                  return (
                    <React.Fragment key={ri}>
                      <tr className={`${r.item.status === 'cancelled' ? 'opacity-50 line-through' : ''} ${selectedKeys.has(makeKey(r.item.id, r.color)) ? 'bg-emerald-50' : 'bg-white'} hover:bg-emerald-50/40 transition-colors`}>
                        <td className="px-2 py-2 text-center border border-gray-500 align-middle" rowSpan="2">
                          <Checkbox
                            checked={selectedKeys.has(makeKey(r.item.id, r.color))}
                            onCheckedChange={() => toggleColorSelect(r.item.id, r.color)}
                            className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                            data-testid={`so-item-checkbox-${r.item.id}-${r.color}`}
                          />
                        </td>
                        <td className="px-3 py-2 text-center text-gray-500 border border-gray-500 font-mono text-xs" rowSpan="2">{r.sr}</td>
                        <td className="px-2 py-1.5 text-center border border-gray-500" rowSpan="2">
                          {firstImg ? <img src={`${API_BASE}${firstImg}`} alt={r.item.design_code} className="w-11 h-11 rounded object-cover cursor-pointer hover:ring-2 hover:ring-emerald-400 transition-all mx-auto" onClick={() => setLightboxImg(`${API_BASE}${firstImg}`)} /> : <div className="w-11 h-11 bg-gray-100 rounded flex items-center justify-center text-gray-400 mx-auto"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>}
                        </td>
                        <td className="px-3 py-2 font-semibold text-gray-900 border border-gray-500" rowSpan="2">{r.item.design_code}</td>
                        <td className="px-3 py-2 text-gray-600 border border-gray-500 text-xs" rowSpan="2">{getBrandName(r.item.brand_id)}</td>
                        <td className="px-3 py-2 font-medium text-gray-800 border border-gray-500" rowSpan="2">{r.color}</td>
                        {activeSizes.map(s => {
                          const q = r.sizes[s] || 0; const d = r.dispatched[s] || 0; const done = d >= q && q > 0;
                          const vIdx = (r.item.variants || []).findIndex(v => v.color === r.color && v.size === s);
                          return <td key={s} className="px-1 py-1 text-center border border-gray-500">{q > 0 ? <div>
                            {canManage && !done && so.status !== 'cancelled' && so.status !== 'closed' ? (
                              <InlineEditCell value={q} onSave={(newVal) => { if (vIdx >= 0) inlineUpdateVariant(r.item.id, vIdx, 'qty_ordered', newVal); }} />
                            ) : <span className={`font-medium ${done ? 'text-green-600' : ''}`}>{q}</span>}
                            {d > 0 && <div className={`text-xs font-medium ${done ? 'text-green-600' : 'text-amber-500'}`}>{d}(D)</div>}
                          </div> : <span className="text-gray-300">-</span>}</td>;
                        })}
                        <td className="px-3 py-2 text-center font-bold text-gray-900 border border-gray-500" rowSpan="2">{r.qty}</td>
                        <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-500" rowSpan="2">{r.amount > 0 ? `₹${Math.round(r.amount).toLocaleString('en-IN')}` : ''}</td>
                        <td className="px-3 py-2 text-center border border-gray-500" rowSpan="2">
                          <span className={`font-medium ${r.totalDispatched >= r.qty && r.qty > 0 ? 'text-green-600' : r.totalDispatched > 0 ? 'text-amber-600' : 'text-gray-400'}`}>{r.totalDispatched}</span>
                        </td>
                        {showActionCol && r.isFirstOfItem && (() => {
                          const itemStatus = r.item.status;
                          const hasPending = (r.item.variants || []).some(v => (v.qty_ordered || 0) > (v.qty_dispatched || 0));
                          const isClosed = itemStatus === 'closed' || itemStatus === 'cancelled' || itemStatus === 'dispatched';
                          return (
                            <td className="px-2 py-2 text-center border border-gray-500 align-middle" rowSpan={r.itemRowSpan}>
                              {isClosed ? (
                                <Badge className="text-[10px] bg-gray-200 text-gray-600">{statusLabel(itemStatus)}</Badge>
                              ) : hasPending ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-[10px] h-7 px-2 border-orange-300 text-orange-700 hover:bg-orange-50"
                                  disabled={closingItemId === r.item.id}
                                  onClick={() => closeItem(r.item.id)}
                                  data-testid={`close-item-${r.item.id}`}
                                >
                                  {closingItemId === r.item.id ? '...' : 'Close Item'}
                                </Button>
                              ) : null}
                            </td>
                          );
                        })()}
                      </tr>
                      <tr className={`${r.item.status === 'cancelled' ? 'opacity-50' : ''} bg-white`}>
                        {(() => {
                          const cells = []; let sIdx = 0;
                          while (sIdx < activeSizes.length) {
                            const s = activeSizes[sIdx]; const q = r.sizes[s] || 0;
                            if (q === 0) { cells.push(<td key={sIdx} className="border border-gray-500"></td>); sIdx++; continue; }
                            const group = r.wspGroups.find(g => g.sizes.includes(s));
                            if (group) { let span = 0; for (let i = sIdx; i < activeSizes.length; i++) { if (group.sizes.includes(activeSizes[i])) span++; else break; } cells.push(<td key={sIdx} colSpan={span} className="px-2 py-1.5 text-center border border-gray-500"><span className="font-semibold text-blue-800 text-xs">@₹{group.wsp.toLocaleString('en-IN')}</span></td>); sIdx += span; }
                            else { cells.push(<td key={sIdx} className="border border-gray-500"></td>); sIdx++; }
                          }
                          return cells;
                        })()}
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-[#218C59] text-white font-bold text-sm">
                  <td className="px-2 py-2.5 border border-green-700"></td>
                  <td className="px-3 py-2.5 border border-green-700" colSpan="4">TOTAL</td>
                  {activeSizes.map(s => { const t = rows.reduce((sum, r) => sum + (r.sizes[s] || 0), 0); return <td key={s} className="px-2 py-2.5 text-center border border-green-700">{t || ''}</td>; })}
                  <td className="px-3 py-2.5 text-center border border-green-700">{grandQty}</td>
                  <td className="px-3 py-2.5 text-right border border-green-700">₹{Math.round(grandAmount).toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2.5 text-center border border-green-700">{rows.reduce((s, r) => s + r.totalDispatched, 0)}</td>
                  {showActionCol && <td className="px-2 py-2.5 border border-green-700"></td>}
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Floating Action Bar for selected items */}
      {selectedKeys.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-4 animate-fadeIn" data-testid="so-item-action-bar">
          <CheckSquare className="w-5 h-5 text-emerald-400" />
          <span className="text-sm font-medium">{selectedKeys.size} colour{selectedKeys.size > 1 ? 's' : ''} selected</span>
          <Button size="sm" variant="ghost"
            className="text-emerald-300 hover:text-emerald-200 hover:bg-emerald-900/30 gap-1.5"
            onClick={processSelectedItems}
            data-testid="so-process-selected-btn"
          >
            <ArrowRightCircle className="w-4 h-4" /> Process Selected
          </Button>
          <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white" onClick={() => setSelectedKeys(new Set())} data-testid="so-item-clear-selection">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={!!lightboxImg} onOpenChange={() => setLightboxImg(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black/90 border-none" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Product Image</DialogTitle>
          <img src={lightboxImg} alt="Full size" className="w-full h-auto max-h-[85vh] object-contain rounded" />
        </DialogContent>
      </Dialog>

      {/* Download Dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Download Sales Order</DialogTitle><DialogDescription>Choose format</DialogDescription></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex gap-3">
              <Button variant={shareFormat === 'pdf' ? 'default' : 'outline'} className={shareFormat === 'pdf' ? 'bg-red-600 hover:bg-red-700' : ''} onClick={() => setShareFormat('pdf')}>PDF</Button>
              <Button variant={shareFormat === 'excel' ? 'default' : 'outline'} className={shareFormat === 'excel' ? 'bg-green-600 hover:bg-green-700' : ''} onClick={() => setShareFormat('excel')}>Excel</Button>
            </div>
            <Button className="w-full" onClick={() => executeShare(shareFormat)} data-testid="so-share-download">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>Download
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesOrderDetail;
